import { get } from "lodash";
import { Control, useWatch } from "react-hook-form";

import useGetExpectations from "~/pages/StaffBonus/hooks/useGetStaffExpectations";
import { useDealerData } from "~/app/Context/DealerContext";
import { FilterFieldOptionsHookResult } from "~/features/filter";
import { useGetLocationData } from "~/hooks/locationData";
import { onScrollBottomDealers } from "~/hooks/selectDealerValue";
import { language } from "~/libs/getData";
import { itemsDataRoute } from "~/pages/Staff/assets/StaffFilter/lib";
import { FILTER_BY } from "~/pages/Staff/assets/StaffFilter/lib";
import {
  DealersLocationItem,
  DealerStatus,
  useGetDealerPanelQuery,
  useGetDealersLocationItemQuery,
} from "~/schemas/generated/graphql";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";
import { stringListToSelectOption } from "~/shared/lib/StringListToSelectOption/stringListToSelectOption";

/**
 * Опции «Значения» зависят от измерения filterBy: дилеры — с пагинацией/поиском,
 * локации — списками из справочника. Измерение читается из текущего значения
 * формы (useWatch), поэтому опции переключаются сразу при выборе в поле
 * «Отфильтровать», не дожидаясь применения фильтра. До выбора в форме
 * (первый рендер) берём применённое значение из URL.
 */
export const useFilterItemOptions = (
  control?: Control,
): FilterFieldOptionsHookResult => {
  const isEn = language === "en";
  const { filterBy: appliedFilterBy, filterItems } =
    useGetLocationData() as Record<string, unknown>;
  const formFilterBy = useWatch({ control, name: "filterBy" });
  const filterBy = (formFilterBy as string) || appliedFilterBy;
  const { DealerId } = useDealerData();
  const sessionDealerId = useCurrentDealerId();
  const currentDealerId = DealerId || sessionDealerId;

  const dealerCity = useGetDealersLocationItemQuery({
    variables: { item: DealersLocationItem["City"] },
  });
  const dealerCountry = useGetDealersLocationItemQuery({
    variables: { item: DealersLocationItem["Country"] },
  });
  const dealerSubject = useGetDealersLocationItemQuery({
    variables: { item: DealersLocationItem["Subject"] },
  });
  const dealerFo = useGetDealersLocationItemQuery({
    variables: { item: DealersLocationItem["Fo"] },
  });

  const dealersInstance = onScrollBottomDealers(DealerStatus.Active);

  // Выбранный по умолчанию дилер может не попасть на первую страницу
  // подгруженного списка — без опции селект покажет сырой id вместо
  // названия. Догружаем его название отдельным запросом (как DealerInfoPanel).
  const selectedDealerIds = String(filterItems ?? "")
    .split(",")
    .filter(Boolean);
  const needCurrentDealer =
    !!currentDealerId && selectedDealerIds.includes(String(currentDealerId));
  const { data: currentDealerData } = useGetDealerPanelQuery({
    variables: { DealerId: currentDealerId },
    skip: !needCurrentDealer,
  });

  switch (filterBy) {
    case FILTER_BY.CITY:
      return {
        options: get(dealerCity, itemsDataRoute(isEn), []).map(
          stringListToSelectOption,
        ),
        loading: dealerCity.loading,
      };
    case FILTER_BY.FO:
      return {
        options: get(dealerFo, itemsDataRoute(isEn), []).map(stringListToSelectOption),
        loading: dealerFo.loading,
      };
    case FILTER_BY.AREA:
      return {
        options: get(dealerSubject, itemsDataRoute(isEn), []).map(
          stringListToSelectOption,
        ),
        loading: dealerSubject.loading,
      };
    case FILTER_BY.COUNTRY:
      return {
        options: get(dealerCountry, itemsDataRoute(isEn), []).map(
          stringListToSelectOption,
        ),
        loading: dealerCountry.loading,
      };
    default: {
      const options = get(dealersInstance, "dealersForSelect", []).map(
        ({ value, label }) => ({ value: String(value), label }),
      );
      if (
        needCurrentDealer &&
        !options.some((option) => option.value === String(currentDealerId))
      ) {
        const dealer = get(currentDealerData, "DealerGetter.getDealer") as
          | { DealerName?: string; DealerNameEn?: string }
          | undefined;
        const dealerName = isEn ? dealer?.DealerNameEn : dealer?.DealerName;
        if (dealerName) {
          options.unshift({
            value: String(currentDealerId),
            label: dealerName,
          });
        }
      }
      return {
        options,
        loading: dealersInstance.GetViolationTypesLoading,
        onScrollBottom: dealersInstance.introspectionHandler,
        searchOptions: dealersInstance.searchDealer,
      };
    }
  }
};

interface ExpectationOptionsData extends FilterFieldOptionsHookResult {
  /** instanceId ожиданий, объединяющих обучение/сертификацию
   * (в старой форме подставлялись в фильтр автоматически). */
  combinableIds: number[];
}

/** Ожидания по должностям (processItems перенесён из старого StaffFilter). */
export const useExpectationOptionsData = (): ExpectationOptionsData => {
  const { data: dataExpectations, isLoading } = useGetExpectations();

  const processItems = (items) =>
    items
      .map((item) => {
        const sortedPositions = [...item.positions].sort((a, b) =>
          a.code.localeCompare(b.code),
        );

        return {
          value: item.instanceId,
          label: sortedPositions
            .map((pos) => `${pos.code} ${pos.name}`)
            .join(", "),
          sortCode: sortedPositions[0]?.code || "",
        };
      })
      .sort((a, b) => a.sortCode.localeCompare(b.sortCode))
      .filter(
        (item, index, self) =>
          index === self.findIndex((el) => el.value === item.value),
      )
      .map((item) => ({ value: item.value, label: item.label }));

  const expectations = dataExpectations?.expectations || [];
  const options = processItems(expectations);
  const combinable = processItems(
    expectations.filter(
      (item) => item.combineTraining || item.combineCertification,
    ),
  );

  return {
    options: options.map(({ value, label }) => ({
      value: String(value),
      label,
    })),
    combinableIds: combinable.map(({ value }) => Number(value)),
    loading: isLoading,
  };
};

export const useExpectationOptions = (): FilterFieldOptionsHookResult => {
  const { options, loading } = useExpectationOptionsData();
  return { options, loading };
};
